/**
 * Layout math for the TV scoreboard (/scoreboard/:pin).
 *
 * The board must be legible from across a bar on a 1080p TV. A row gets
 * too small past 12 per column, so the board uses these tiers:
 *   1 to 10 teams   one column. Rows are sized for at least 5 rows.
 *   11 to 24 teams  two columns, up to 12 rows each.
 *   25+ teams       two columns of 12 rows. Pages of 24 rotate.
 */

export const MAX_ROWS_PER_COLUMN = 12;
const SINGLE_COLUMN_MAX = 10;
const MIN_ROWS = 5;

export interface ScoreboardLayout {
  columns: 1 | 2;
  rowsPerColumn: number;
  pageSize: number;
  pageCount: number;
}

export function scoreboardLayout(teamCount: number): ScoreboardLayout {
  const count = Math.max(0, Math.floor(teamCount));
  if (count <= SINGLE_COLUMN_MAX) {
    const rows = Math.max(count, MIN_ROWS);
    return { columns: 1, rowsPerColumn: rows, pageSize: rows, pageCount: 1 };
  }
  const pageSize = MAX_ROWS_PER_COLUMN * 2;
  if (count <= pageSize) {
    return { columns: 2, rowsPerColumn: Math.ceil(count / 2), pageSize, pageCount: 1 };
  }
  return {
    columns: 2,
    rowsPerColumn: MAX_ROWS_PER_COLUMN,
    pageSize,
    pageCount: Math.ceil(count / pageSize),
  };
}

/** The items on page `page` (0-based). A page past the end wraps to 0. */
export function pageSlice<T>(items: T[], page: number, pageSize: number): T[] {
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const p = ((page % pageCount) + pageCount) % pageCount;
  return items.slice(p * pageSize, (p + 1) * pageSize);
}

/** Grid slot for the i-th row on a page. Rows fill column 0 first. */
export function slotFor(index: number, rowsPerColumn: number): { col: number; row: number } {
  return { col: Math.floor(index / rowsPerColumn), row: index % rowsPerColumn };
}

export type RankMove = 'up' | 'down';

/**
 * Compare two boards by team id. A team with a better (lower) rank than
 * before moved 'up'. A new team gets no move.
 */
export function rankMoves(
  prev: ReadonlyArray<{ teamId: string; rank: number }>,
  next: ReadonlyArray<{ teamId: string; rank: number }>,
): Record<string, RankMove> {
  const before = new Map(prev.map((s) => [s.teamId, s.rank]));
  const moves: Record<string, RankMove> = {};
  for (const s of next) {
    const old = before.get(s.teamId);
    if (old === undefined || old === s.rank) continue;
    moves[s.teamId] = s.rank < old ? 'up' : 'down';
  }
  return moves;
}

/** 1 -> "1st", 2 -> "2nd", 11 -> "11th", 22 -> "22nd". */
export function ordinal(rank: number): string {
  const mod10 = rank % 10;
  const mod100 = rank % 100;
  if (mod10 === 1 && mod100 !== 11) return `${rank}st`;
  if (mod10 === 2 && mod100 !== 12) return `${rank}nd`;
  if (mod10 === 3 && mod100 !== 13) return `${rank}rd`;
  return `${rank}th`;
}
